import { deleteDoc, doc } from "firebase/firestore";
import {
  FIREBASE_AUTH_INACTIVE_MESSAGE,
  INVALID_FIRESTORE_PATH_MESSAGE,
  SECURITY_RULES_DENIED_MESSAGE,
  SECURITY_RULES_RESPONDED_MESSAGE,
  WRITE_TESTS_SKIPPED_MESSAGE,
} from "./firestoreTestMessages";

/** Shared sandbox for Health Check, Deep Test, stress/benchmark probes. */
export const HEALTH_TEST_COLLECTION = "studio_runtime_test";

/**
 * Single source of truth for temporary Firestore test collection names.
 * Health Check and Deep Test must always use this collection.
 */
export function healthTestCollectionName() {
  return HEALTH_TEST_COLLECTION;
}

/** Collections tried for read-only Firestore reachability (no reserved __* IDs). */
export function healthCheckReadProbeCollections() {
  return [healthTestCollectionName(), "studio_health"];
}

/** Firestore reserves collection IDs that start and end with "__". */
export function isReservedFirestoreCollectionId(collectionId) {
  const name = String(collectionId || "").trim();
  return name.startsWith("__") && name.endsWith("__");
}

export function createHealthTestDocId(prefix = "health_check") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export {
  FIREBASE_AUTH_INACTIVE_MESSAGE,
  AUTH_SESSION_NOT_FOUND_MESSAGE,
  SECURITY_RULES_DENIED_MESSAGE,
  INVALID_FIRESTORE_PATH_MESSAGE,
  RUNTIME_MISMATCH_MESSAGE,
  AUTH_RUNTIME_MISMATCH_MESSAGE,
} from "./firestoreTestMessages";

/**
 * Classify Firestore errors from health/deep tests.
 * Unauthenticated + PERMISSION_DENIED → auth inactive (not security rules).
 * Authenticated + PERMISSION_DENIED → security rules.
 * INVALID_ARGUMENT → invalid collection/document path.
 */
export function classifyFirestoreTestError(err, { authenticated = false, connectionOnly = false, forWrite = false } = {}) {
  const code = String(err?.code || "").toLowerCase();
  const message = String(err?.message || err || "");
  const numericCode = err?.code;

  const isPermissionDenied =
    code === "permission-denied" ||
    numericCode === 7 ||
    numericCode === "7" ||
    /permission.?denied/i.test(message) ||
    /insufficient permissions/i.test(message);

  if (isPermissionDenied) {
    if (forWrite && !authenticated) {
      return {
        kind: "write_skipped",
        status: "skipped",
        code: "no-session",
        message: WRITE_TESTS_SKIPPED_MESSAGE,
        userMessage: WRITE_TESTS_SKIPPED_MESSAGE,
      };
    }
    if (connectionOnly && !authenticated) {
      return {
        kind: "rules_responded",
        status: "success",
        code: "permission-denied",
        message: SECURITY_RULES_RESPONDED_MESSAGE,
        userMessage: SECURITY_RULES_RESPONDED_MESSAGE,
      };
    }
    if (!authenticated) {
      return {
        kind: "unauthenticated",
        status: "failed",
        code: "unauthenticated",
        message: FIREBASE_AUTH_INACTIVE_MESSAGE,
        userMessage: FIREBASE_AUTH_INACTIVE_MESSAGE,
      };
    }
    return {
      kind: "permission_denied",
      status: "permission_denied",
      code: "permission-denied",
      message: SECURITY_RULES_DENIED_MESSAGE,
      userMessage: SECURITY_RULES_DENIED_MESSAGE,
    };
  }

  const isInvalidArgument =
    code === "invalid-argument" ||
    numericCode === 3 ||
    numericCode === "3" ||
    /invalid.?argument/i.test(message);

  if (isInvalidArgument) {
    return {
      kind: "invalid_argument",
      status: "failed",
      code: "invalid-argument",
      message: INVALID_FIRESTORE_PATH_MESSAGE,
      userMessage: INVALID_FIRESTORE_PATH_MESSAGE,
    };
  }

  return {
    kind: "other",
    status: "failed",
    code: code || "unknown",
    message: message || "Firestore operation failed",
    userMessage: message || "Firestore operation failed",
  };
}

export async function deleteHealthTestDocument(firestore, docId, collectionName = healthTestCollectionName()) {
  if (!firestore || !docId) return false;
  try {
    await deleteDoc(doc(firestore, collectionName, docId));
    return true;
  } catch {
    return false;
  }
}

/** Best-effort removal of every temporary health-test document. */
export async function cleanupHealthTestDocuments(
  firestore,
  docIds,
  collectionName = healthTestCollectionName()
) {
  if (!firestore || !Array.isArray(docIds) || !docIds.length) return 0;
  const results = await Promise.allSettled(
    docIds.map((id) => deleteDoc(doc(firestore, collectionName, id)))
  );
  return results.filter((r) => r.status === "fulfilled").length;
}
